import { memo, useState } from 'react';
import type { CSSProperties } from 'react';
import { flushSync } from 'react-dom';
import { ActionIcon } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { Rnd } from 'react-rnd';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { StampPreview } from '@dealstack/frontend/shared/components/StampPreview/StampPreview.tsx';
import {
	computePreviewScale,
	expectedAspectRatio,
	minHeightFor,
	MIN_STAMP_WIDTH_PT,
	pdfToBrowser,
	resizedBoxOnRatio,
} from './placement.utils.ts';
import { maxResizeWidth } from './placementCollision.utils.ts';
import type { ResizeDirection, ViewportPoint } from './placementCollision.types.ts';
import { A11yLabel, Shell } from './PlacementBox.styled.ts';
import type { PlacementBoxBounds, ResizeLimit } from './PlacementBox.types.ts';
import type { CanvasPlacement, PageDimensionsPt, SlotMeta } from './PlacementCanvas.types.ts';
import type { PlacementGestureHandlers } from './usePlacementGestures.types.ts';

interface Props {
	slotKey: string;
	placement: CanvasPlacement;
	/** Missing for a key no slot describes: the box still renders, labelled with the key. */
	meta?: SlotMeta;
	/** The page the box sits on. */
	dims: PageDimensionsPt;
	/** Canvas scale; maps the min-size floor to the backend's
	 *  MIN_STAMP_WIDTH_PT/HEIGHT_PT regardless of zoom. */
	scale: number;
	showMetadata: boolean;
	isSelected: boolean;
	/** Overlaps another box with nowhere free to go. */
	isOverlapping: boolean;
	/** Dragged over another box: drawn see-through while the landing preview shows. */
	isDropBlocked: boolean;
	/** The page's drag, drop and resize handling, shared by every box on it. */
	gestures: PlacementGestureHandlers;
	onSelect: (slotKey: string) => void;
	/** When provided and the box is selected, shows a × to drop the placement
	 *  (not persisted until the editor's Save). */
	onRemove?: (slotKey: string) => void;
}

const REMOVE_BUTTON_STYLE: CSSProperties = {
	position: 'absolute',
	top: '-8px',
	right: '-8px',
	zIndex: 4,
	pointerEvents: 'auto',
};

/** One placed slot's stamp box, draggable across pages and resizable on its locked ratio.
 *  Memoised: the page's drop preview changes on every drag tick, and the other boxes need not follow. */
export const PlacementBox = memo(function PlacementBox({
	slotKey,
	placement,
	meta,
	dims,
	scale,
	showMetadata,
	isSelected,
	isOverlapping,
	isDropBlocked,
	gestures,
	onSelect,
	onRemove,
}: Props) {
	const [resizeLimit, setResizeLimit] = useState<ResizeLimit | null>(null);
	const label = meta?.label ?? slotKey;
	const isWitness = meta?.isWitness ?? false;
	const browserBox = pdfToBrowser(placement.pdfBox, dims.heightPt, scale);
	const bounds: PlacementBoxBounds = {
		width: dims.widthPt * scale,
		height: dims.heightPt * scale,
	};
	const selectSlot = () => onSelect(slotKey);
	const minW = MIN_STAMP_WIDTH_PT * scale;
	// A witness box is locked to a taller ratio — its stamp carries the address and
	// occupation rows a party stamp doesn't, and the renderer's row budget depends on the
	// ratio alone, so a witness box drawn at 2.0 could never fit them however big it was.
	const minH = minHeightFor(showMetadata, isWitness) * scale;
	const lockRatio = expectedAspectRatio(showMetadata, isWitness);
	const centreOf = (node: HTMLElement): ViewportPoint => {
		const rect = node.getBoundingClientRect();
		return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
	};

	const handleDrag = (_e: unknown, data: { node?: HTMLElement }) => {
		if (data.node) {
			gestures.previewPlacementDrop(slotKey, browserBox, centreOf(data.node));
		}
	};

	// The absolute drop centre, not react-rnd's local position: the drop may land on another page.
	const handleDragStop = (_e: unknown, data: { node?: HTMLElement }) => {
		if (data.node) {
			gestures.commitPlacementDrop(slotKey, browserBox, centreOf(data.node));
		}
	};

	const handleResizeStart = (_e: unknown, direction: ResizeDirection) => {
		selectSlot();
		const maxWidth = maxResizeWidth({
			box: browserBox,
			direction,
			ratio: lockRatio,
			obstacles: gestures.findResizeObstacles(slotKey),
			bounds,
		});
		// Synchronous: react-rnd copies the max size from props straight after this returns, and
		// holds that copy for the whole gesture.
		flushSync(() => setResizeLimit({ maxWidth, maxHeight: maxWidth / lockRatio }));
	};

	const handleResizeStop = (
		_e: unknown,
		_dir: unknown,
		ref: HTMLElement,
		_delta: unknown,
		position: { x: number; y: number }
	) => {
		const width = parseFloat(ref.style.width);
		setResizeLimit(null);
		// Height derived from the width, not read back from the DOM: react-rnd rounds the
		// locked pair to whole pixels and a witness box lands off its ratio (DC-1163).
		gestures.commitPlacementResize(
			slotKey,
			resizedBoxOnRatio({
				width: Math.min(width, resizeLimit?.maxWidth ?? width),
				ratio: lockRatio,
				position,
				bounds,
			})
		);
	};

	return (
		<Rnd
			size={{ width: browserBox.width, height: browserBox.height }}
			position={{ x: browserBox.left, y: browserBox.top }}
			lockAspectRatio={lockRatio}
			minWidth={minW}
			minHeight={minH}
			maxWidth={resizeLimit?.maxWidth}
			maxHeight={resizeLimit?.maxHeight}
			onDragStart={selectSlot}
			onDrag={handleDrag}
			onResizeStart={handleResizeStart}
			onDragStop={handleDragStop}
			onResizeStop={handleResizeStop}
			style={{ zIndex: isSelected ? 3 : 2 }}
		>
			<Shell
				$selected={isSelected}
				$overlapping={isOverlapping}
				$dropBlocked={isDropBlocked}
				onMouseDown={selectSlot}
				data-testid={TEST_ELEMENT_IDS.PLACEMENT_EDITOR_BOX}
				data-drop-blocked={isDropBlocked}
				data-overlapping={isOverlapping}
			>
				<StampPreview
					slotLabel={label}
					isWitness={isWitness}
					roleType={meta?.roleType}
					kind={meta?.kind ?? 'signature'}
					showMetadata={showMetadata}
					scale={computePreviewScale(browserBox.width)}
				/>
				<A11yLabel>{label} (placed)</A11yLabel>
				{isSelected && onRemove ? (
					<ActionIcon
						variant="filled"
						color="red"
						size="xs"
						radius="xl"
						aria-label="Remove placement"
						data-testid={TEST_ELEMENT_IDS.PLACEMENT_EDITOR_BOX_REMOVE}
						style={REMOVE_BUTTON_STYLE}
						onMouseDown={(e) => e.stopPropagation()}
						onClick={(e) => {
							e.stopPropagation();
							onRemove(slotKey);
						}}
					>
						<IconX size={12} />
					</ActionIcon>
				) : null}
			</Shell>
		</Rnd>
	);
});
